import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { NavLink } from "react-router-dom";
import { Settings, Users, FileText, Shield, ShoppingBag } from "lucide-react";
import { useRecentProductsStore } from "@/stores/recent-products.store";
import { Plan } from "@/types/plan.type";
import { planProxy, usePlanSnapshot } from "@/stores/plans.store";
import { userProxy, useUserSnapshot } from "@/stores/user.store";
import { useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { QuickAccessCard } from "@/components/heka/navigation/quick-access-card";
import { ServiceItem } from "@/components/heka/navigation/service-item";
import { StyledIcon } from "@/components/heka/styled-icon";

export default function HomePage() {
  const { recentProductsSnapshot } = useRecentProductsStore();
  const planSnapshot = usePlanSnapshot();
  const userSnapshot = useUserSnapshot();

  useEffect(() => {
    planProxy.fetchPlans(0);
    userProxy.fetchUsersCount();
  }, []);

  return (
    <div className="p-6 pb-16 space-y-6">
      <h1 className="text-2xl font-semibold mb-6">Home</h1>
      <div>
        <h2 className="text-xl font-semibold mb-4">Quick access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <QuickAccessCard
            title="Organization"
            icon={<Shield className="w-5 h-5" />}
            to="/organization"
          />
          <QuickAccessCard
            title="Products"
            icon={<ShoppingBag className="w-5 h-5" />}
            to="/products"
          />
          <QuickAccessCard
            title="Users"
            icon={<Users className="w-5 h-5" />}
            to="/users"
          />
          <QuickAccessCard
            title="Settings"
            icon={<Settings className="w-5 h-5" />}
            to="/settings"
          />
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-medium">
            Recently visited products
          </CardTitle>
          <NavLink to="/products" className="inline-block">
            <Button variant="ghost" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              View all products
            </Button>
          </NavLink>
        </CardHeader>
        <CardContent className="space-y-1">
          {recentProductsSnapshot.recentProducts.length > 0 ? (
            recentProductsSnapshot.recentProducts.map((product: Plan) => (
              <ServiceItem
                key={product.code}
                name={product.name}
                icon={<StyledIcon icon={ShoppingBag} color="bg-primary" />}
                to={`/products/${product.code}`}
              />
            ))
          ) : (
            <p className="text-sm text-muted-foreground p-4">
              No recently visited products.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium">
            Resource Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <h3 className="font-medium mb-3">Products</h3>
              {planSnapshot.isLoading ? (
                <Skeleton className="h-8 w-1/4 mb-2" />
              ) : (
                <p className="text-3xl font-bold mb-2">
                  {planSnapshot.plans.metadata.count}
                </p>
              )}
              {planSnapshot.isLoading ? (
                <Skeleton className="h-4 w-1/2 mb-4" />
              ) : (
                <p className="text-muted-foreground mb-4">
                  {planSnapshot.plans.metadata.count <= 1
                    ? "Available product"
                    : "Available products"}
                </p>
              )}
            </div>
            <Separator className="hidden lg:block" orientation="vertical" />
            <div className="flex-1">
              <h3 className="font-medium mb-3">Users</h3>
              {userSnapshot.isLoading || userSnapshot.isLoadingCount ? (
                <Skeleton className="h-8 w-1/4 mb-2" />
              ) : (
                <p className="text-3xl font-bold mb-2">
                  {userSnapshot.usersCount}
                </p>
              )}
              {userSnapshot.isLoading || userSnapshot.isLoadingCount ? (
                <Skeleton className="h-4 w-1/2 mb-4" />
              ) : (
                <p className="text-muted-foreground mb-4">
                  {userSnapshot.usersCount === 1
                    ? "User in your organization"
                    : "Users in your organization"}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
